import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export async function fetchAcceptedMembershipOrgIds(volunteerId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('organization_memberships')
    .select('organization_id')
    .eq('volunteer_id', volunteerId)
    .eq('status', 'accepted');
  if (error) throw error;
  return (data ?? []).map((row) => (row as { organization_id: string }).organization_id);
}

export async function hasAcceptedOrgMembership(
  volunteerId: string,
  organizationId: string
): Promise<boolean> {
  const { data, error } = await supabase
    .from('organization_memberships')
    .select('id')
    .eq('volunteer_id', volunteerId)
    .eq('organization_id', organizationId)
    .eq('status', 'accepted')
    .maybeSingle();
  if (error) return false;
  return data != null;
}

/**
 * Organization IDs where the volunteer has status = accepted (plain members + admins).
 */
export function useAcceptedMembershipOrgIds(userId: string | null) {
  const [orgIds, setOrgIds] = useState<string[]>([]);

  const refetch = useCallback(async () => {
    if (!userId) {
      setOrgIds([]);
      return;
    }
    try {
      setOrgIds(await fetchAcceptedMembershipOrgIds(userId));
    } catch {
      setOrgIds([]);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setOrgIds([]);
      return;
    }
    let cancelled = false;
    fetchAcceptedMembershipOrgIds(userId)
      .then((ids) => {
        if (!cancelled) setOrgIds(ids);
      })
      .catch(() => {
        if (!cancelled) setOrgIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return { orgIds, refetch };
}
